import React, {Component, ReactNode} from 'react'
import {ReservationStatusCardInformation} from '../AdditionalChargeCardInformation'
import ReservationCardInformation from '../ReservationCardInformation'
import {getFacilityCategoryImageUrl} from '../../facilities/FacilityCategoryImageHelper'
import {
    ColumnFlexContainer,
    FullWidthLeftColumnFlexContainer,
    FullWidthRowFlexContainer,
} from '../../../styledComponents/FlexComponents'
import ReservationStatusTag from './ReservationStatusTag'
import {RESERVATION_STATUS} from '../../../CodeMaster'
import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import styled, {css, keyframes} from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {CardActionArea} from '@mui/material'
import {CoverImage, EllipsisText, StyledCard} from './CommonStyle'

interface Props {
    cardInformation: ReservationStatusCardInformation
    onClick: () => void
    highlight?: boolean
}

class ReservationCard extends Component<Props> {

    getChargeNode(info: ReservationStatusCardInformation): ReactNode {
        if (info.reserveStatus !== RESERVATION_STATUS.ELECTION) {
            return null
        }
        const charge = info.chargeInformations
            .reduce((subCharge: number, chargeInfo) => subCharge + chargeInfo.charge, 0)
        return <Charge>{formatNumberWithCommas(charge)}円</Charge>
    }

    render(): ReactNode {
        const {cardInformation, onClick} = this.props
        const {facilityCategory, facilityName, imageUrl, reserveTitle, reserveStatus} = cardInformation

        const imageSrc = imageUrl || getFacilityCategoryImageUrl(facilityCategory)

        let reserverNameNode: ReactNode = null
        let useDurationString: string
        if (cardInformation instanceof ReservationCardInformation) {
            reserverNameNode = cardInformation.reserverName === null ? null
                : <div>{cardInformation.reserverName} さんが予約</div>

            const useStartDatetimeString = cardInformation.reserveStartDatetime.format('M月D日(ddd) H:mm')
            let useEndDatetimeString: string
            if (cardInformation.reserveStartDatetime.format('YYYY-MM-DD') ===
                cardInformation.reserveEndDatetime.format('YYYY-MM-DD')) {
                useEndDatetimeString = cardInformation.reserveEndDatetime.format('H:mm')
            } else {
                useEndDatetimeString = cardInformation.reserveEndDatetime.format('M月D日(ddd) H:mm')
            }
            useDurationString = `${useStartDatetimeString} 〜 ${useEndDatetimeString}`
        } else {
            useDurationString = cardInformation.useDate.format('M月D日(ddd)')
        }

        return (<>
            <StyledCard>
                <CardActionArea onClick={onClick}>
                    <StyledReservationCard>
                        <FullWidthRowFlexContainer>
                            <MainLeftArea>
                                <ColumnFlexContainer>
                                    <ReservationStatusTag reservationStatus={reserveStatus}/>
                                    <BlockSpacer height={'2px'}/>
                                    <FacilityImage url={imageSrc}/>
                                </ColumnFlexContainer>
                            </MainLeftArea>
                            <MainRightArea>
                                <FullWidthLeftColumnFlexContainer>
                                    <ReserverArea>
                                        {reserverNameNode}
                                    </ReserverArea>
                                    <FacilityNameArea>
                                        <FacilityNameAreaInner>
                                            <EllipsisText
                                                lineHeightInPixel={20}
                                                numOfLines={2}
                                            >
                                                {facilityName}
                                            </EllipsisText>
                                        </FacilityNameAreaInner>
                                    </FacilityNameArea>
                                    <FullWidthRowFlexContainer>
                                        <ReserveTitle>
                                            <EllipsisText
                                                lineHeightInPixel={14}
                                                numOfLines={1}
                                            >
                                                {reserveTitle}
                                            </EllipsisText>
                                        </ReserveTitle>
                                        {this.getChargeNode(cardInformation)}
                                    </FullWidthRowFlexContainer>
                                </FullWidthLeftColumnFlexContainer>
                            </MainRightArea>
                        </FullWidthRowFlexContainer>
                        <DurationArea highlight={this.props.highlight}>
                            {useDurationString}
                        </DurationArea>
                    </StyledReservationCard>
                </CardActionArea>
            </StyledCard>
        </>)
    }
}

export default ReservationCard

const StyledReservationCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: left;
  align-items: flex-start;
`

const MainLeftArea = styled.div`
  flex: 0 0 auto;
  box-sizing: border-box;
  height: 100px;
  padding: 5px 0 5px 5px;
`

const MainRightArea = styled.div`
  flex: 1 1 0%;
  box-sizing: border-box;
  height: 100px;
  padding: 8px 10px 10px 10px;
`

const ReserverArea = styled.div`
  height: 12px;
  font-size: ${FontSize.Small};
  opacity: 0.8;
`

const FacilityNameArea = styled.div`
  display: table;
  margin: 8px 0;
  height: 40px;
`

const FacilityNameAreaInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  color: ${Colors.Body};
  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
`

const ReserveTitle = styled.div`
  flex: 1 1 0%;
  font-size: ${FontSize.Middle};
  opacity: 0.8;
`

const Charge = styled.div`
  flex: 0 0 70px;
  height: 14px;
  line-height: 14px;
  text-align: right;
  font-size: ${FontSize.Middle};
  font-weight: ${FontWeights.Bold};
`

const durationAreaBgColor = css`
  rgba(217, 217, 217, 0.25);
`
const fadeHighlight = keyframes`
  0% {
    background-color: #f8c46c;
  }
  100% {
    background-color: ${durationAreaBgColor};
  }
`

const DurationArea = styled.div<{ highlight?: boolean }>`
  height: 30px;
  width: 100%;
  ${props => {
    if (props.highlight) {
      return css`
        animation: ${fadeHighlight};
        animation-delay: 500ms;
        animation-duration: 2500ms;
        background-color: ${durationAreaBgColor};
      `
    } else {
      return css`
        background-color: ${durationAreaBgColor};
      `
    }
  }};
  text-align: center;
  line-height: 30px;
  color: ${Colors.Body};
  font-size: ${FontSize.Middle};
  font-weight: ${FontWeights.Normal};
`

const FacilityImage = styled(CoverImage)`
  width: 70px;
  height: 70px;
`
